/**
 * In-app live preview / run surface for a Report (obj_type "R") — hosts the
 * shared ReportViewer over the platform `/api/report-data/*` endpoints.
 * Used by the workbench Live Preview tab and the /run EndUserAppView; the
 * downloadable runtime client mounts the same ReportViewer with its own
 * adapter.
 */
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { ReportViewer } from '@twasta/page-runtime/report'
import type {
  ReportDefinition, ReportOutputFormat, ReportRenderOutcome, ReportRuntimeAdapter,
} from '@twasta/page-runtime/report'
import { makeVisualCriteriaRuntime } from '@/api/visualData'
import { fetchReportDefinition, runReportJob } from '@/api/reportData'
import { platformCustomizeTransport, useCanCustomize } from '@/livepreview/runtime/customizeTransport'

export function makeReportRuntimeAdapter(project: string, objName: string): ReportRuntimeAdapter {
  return {
    async fetchDefinition(): Promise<ReportDefinition> {
      const d = await fetchReportDefinition({ project, obj_name: objName })
      return {
        promptVars: d.promptVars || [],
        resolvedParams: d.resolvedParams || {},
        needsInput: !!d.needsInput,
        missingParams: d.missingParams || [],
        defaultFormat: d.defaultFormat || 'html',
        formats: d.formats || ['html', 'pdf'],
        title: d.title || objName,
      }
    },
    // Every format goes through the render JOB (submit → poll → download):
    // a long report outlives the proxy read timeout in front of the backend,
    // which surfaced as a bare 504 on both Run and Export. Each request on
    // this path returns in milliseconds; the viewer's own spinner covers the
    // wait exactly as it did when the render was one long request.
    async render(
      params: Record<string, unknown>, format: ReportOutputFormat,
    ): Promise<ReportRenderOutcome> {
      const out = await runReportJob({
        project, obj_name: objName, params, output_format: format,
      })
      if (out.needsInput || !out.blob) {
        return {
          needsInput: out.needsInput, missingParams: out.missingParams,
          criteriaErrors: out.criteriaErrors,
        }
      }
      if (format === 'html') {
        return { needsInput: false, missingParams: [], html: await out.blob.text() }
      }
      return {
        needsInput: false, missingParams: [], blob: out.blob, filename: out.filename,
      }
    },
    criteria: makeVisualCriteriaRuntime({
      project, kind: 'report', obj_name: objName,
      notify: {
        success: (m: string) => toast.success(m),
        error: (m: string) => toast.error(m),
      },
    }),
  }
}

export function ReportLivePreviewPane({ project, objName, customizable = false }: {
  project: string; objName: string
  /** Offer the ⋮ "Customize" entry (Run view) to a caller who may customize. */
  customizable?: boolean
}) {
  const adapter = useMemo(() => makeReportRuntimeAdapter(project, objName), [project, objName])
  const canCustomize = useCanCustomize(project, customizable)
  const customizeTransport = useMemo(() => platformCustomizeTransport(project), [project])
  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--color-bg)' }}>
      <ReportViewer
        key={`${project}/${objName}`}
        project={project}
        objName={objName}
        adapter={adapter}
        customize={customizable ? { transport: customizeTransport, enabled: canCustomize } : undefined}
      />
    </div>
  )
}

export default ReportLivePreviewPane
